import { UserPlus, Users } from 'lucide-react';
import { useMemo } from 'react';
import { useGetMany, useRecordContext, useTranslate } from 'ra-core';
import { useCanAccessUsersResource } from '../../auth/useCanAccessUsersResource';
import { DataTable } from '../../components/admin/data-table';
import { DateField } from '../../components/admin/date-field';
import { ActionLink } from '../../components/custom/ActionLink';
import { RowActions } from '../../components/custom/RowActions';
import { MoonstarList } from '../Moonstar/MoonstarList';
import { RULE_TYPES, OPERATORS } from './userGroupRuleConstants';

export const UserGroupList = () => (
  <MoonstarList sort={{ field: 'createdOn', order: 'DESC' }}>
    <DataTable bulkActionButtons={false}>
      <DataTable.Col source="name" />
      <DataTable.Col source="source" />
      <DataTable.Col source="createdOn">
        <DateField source="createdOn" />
      </DataTable.Col>
      <DataTable.Col label="resources.user-groups.fields.rules">
        <UserGroupRulesField />
      </DataTable.Col>
      <DataTable.Col className="w-px whitespace-nowrap">
        <UserGroupActions />
      </DataTable.Col>
    </DataTable>
  </MoonstarList>
);

const OPERATOR_LABELS: Record<string, string> = {
  [OPERATORS.IN]: 'Is one of',
  [OPERATORS.NOT_IN]: 'Is not one of',
  [OPERATORS.ANY_IN]: 'Has any of',
  [OPERATORS.NOT_ANY_IN]: 'Has none of',
  [OPERATORS.ALL_IN]: 'All are in',
  [OPERATORS.NOT_ALL_IN]: 'Some are not in',
};

const UserGroupRulesField = () => {
  const record = useRecordContext();
  const ruleValue = record?.rules?.value;
  const jobTitleIds = useMemo(() => {
    if (!ruleValue) return [];
    if (ruleValue.type !== RULE_TYPES.BY_JOB_TITLE) return [];

    return ruleValue.values as number[];
  }, [ruleValue]);

  const unitIds = useMemo(() => {
    if (!ruleValue) return [];
    if (ruleValue.type !== RULE_TYPES.BY_UNITS) return [];

    return ruleValue.values as number[];
  }, [ruleValue]);

  const { data: jobTitles, isPending: jobTitlesIsPending } = useGetMany(
    'jobTitles',
    { ids: jobTitleIds },
    { enabled: jobTitleIds.length > 0 }
  );

  const { data: units, isPending: unitsIsPending } = useGetMany(
    'units',
    { ids: unitIds },
    { enabled: unitIds.length > 0 }
  );

  if (!ruleValue?.type) {
    return <span className="text-sm">—</span>;
  }

  if (ruleValue.type === RULE_TYPES.INCLUDE_ALL) {
    return <span className="text-sm">Include all members</span>;
  }

  if (ruleValue.type === RULE_TYPES.BY_JOB_TITLE) {
    const operatorLabel =
      OPERATOR_LABELS[ruleValue.operator as string] ?? ruleValue.operator;

    let jobTitleNames = '';
    if (jobTitlesIsPending) {
      jobTitleNames = '...';
    } else if (jobTitles && jobTitles.length > 0) {
      jobTitleNames = jobTitles.map((jt) => jt.name).join(', ');
    }

    return (
      <span className="text-sm">
        By job title: {operatorLabel} {jobTitleNames}
      </span>
    );
  } else if (ruleValue.type === RULE_TYPES.BY_UNITS) {
    const operatorLabel =
      OPERATOR_LABELS[ruleValue.operator as string] ?? ruleValue.operator;

    let unitNames = '';
    if (unitsIsPending) {
      unitNames = '...';
    } else if (units && units.length > 0) {
      unitNames = units.map((unit) => unit.name).join(', ');
    }

    return (
      <span className="text-sm">
        By unit: {operatorLabel} {unitNames}
      </span>
    );
  }

  return <span className="text-sm">—</span>;
};

const UserGroupActions = () => {
  const translate = useTranslate();
  const record = useRecordContext();
  const canAccessUsers = useCanAccessUsersResource();

  if (!record || !canAccessUsers) {
    return null;
  }

  return (
    <RowActions>
      <ActionLink to={`/user-groups/${record.id}/members`} icon={<Users />}>
        {translate('moonstar.user-group-members.title')}
      </ActionLink>
      <ActionLink
        to={`/user-groups/${record.id}/members/add`}
        icon={<UserPlus />}
      >
        {translate('moonstar.user-group-members.add.label')}
      </ActionLink>
    </RowActions>
  );
};
